import type { FormEvent } from 'react'
import type { Deal } from '@/constants/crm'
import type { DialogDefaults } from '@/stores/crm-store'

import { useState } from 'react'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import FormSection from '@/components/companies/new-company/form-section'
import { Input } from '@/components/ui/input'
import { CompanySelect, OptionSelect, OwnerSelect } from '@/components/generals/form-selects'
import { OWNERS } from '@/constants/companies'
import { DEAL_STAGES, PIPELINES } from '@/constants/crm'
import { apiRequest } from '@/lib/client-api'
import { addDays } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

type FormState = {
    name: string
    companyId: string
    owner: string
    stage: string
    pipeline: string
    value: string
    closeDate: string
    nextStep: string
}

const STAGE_OPTIONS = DEAL_STAGES.map(stage => stage.value)
const PIPELINE_OPTIONS = PIPELINES.map(pipeline => pipeline.label)

function initialForm(deal: Deal | undefined, defaults: DialogDefaults, ownerOfCompany: string | undefined, today: string): FormState {
    if (deal) {
        return {
            name: deal.name,
            companyId: deal.companyId ?? '',
            owner: deal.owner || OWNERS[0].name,
            stage: deal.stage,
            pipeline: deal.pipeline,
            value: String(deal.value),
            closeDate: deal.closeDate,
            nextStep: deal.nextStep
        }
    }

    return {
        name: '',
        companyId: defaults.companyId ?? '',
        owner: ownerOfCompany && OWNERS.some(owner => owner.name === ownerOfCompany) ? ownerOfCompany : OWNERS[0].name,
        stage: defaults.stage ?? DEAL_STAGES[0].value,
        pipeline: defaults.pipeline ?? PIPELINES[0].label,
        value: '',
        closeDate: addDays(today, 30),
        nextStep: ''
    }
}

function DealForm() {
    const today = useCrmStore(state => state.today)
    const defaults = useCrmStore(state => state.dialogDefaults)
    const deals = useCrmStore(state => state.deals)
    const companies = useCrmStore(state => state.companies)
    const upsertDeal = useCrmStore(state => state.upsertDeal)
    const closeDialog = useCrmStore(state => state.closeDialog)
    const showNotice = useCrmStore(state => state.showNotice)
    const editing = defaults.dealId ? deals.find(deal => deal.id === defaults.dealId) : undefined
    const companyOwner = companies.find(company => company.id === defaults.companyId)?.owner
    const [form, setForm] = useState<FormState>(() => initialForm(editing, defaults, companyOwner, today))
    const [errors, setErrors] = useState<{ name?: string, companyId?: string }>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm(current => ({ ...current, [key]: value }))
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        if (submitting) return

        const nextErrors = {
            name: form.name.trim() ? undefined : "Enter a deal name.",
            companyId: form.companyId ? undefined : "Choose the company this deal is with."
        }

        setErrors(nextErrors)

        if (nextErrors.name || nextErrors.companyId) return

        setSubmitting(true)
        setFormError(null)

        const body = { ...form, name: form.name.trim(), nextStep: form.nextStep.trim() }
        const result = editing
            ? await apiRequest<{ deal: Deal }>(`/api/deals/${editing.id}`, 'PATCH', body)
            : await apiRequest<{ deal: Deal }>('/api/deals', 'POST', body)

        setSubmitting(false)

        if (!result.ok) {
            setFormError(result.message)

            return
        }

        upsertDeal(result.data.deal)
        closeDialog()
        showNotice('success', editing ? "Deal updated." : "Deal added to the board.")
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex flex-col' aria-busy={submitting}>
            <DialogHeader>
                <DialogTitle>{editing ? "Edit Deal" : "New Deal"}</DialogTitle>
                <DialogDescription>
                    {editing ? "Update the deal details. Changes save for the whole team." : "Add a deal to the board. It lands in the stage you pick."}
                </DialogDescription>
            </DialogHeader>

            <FormSection title="Deal">
                <Field label="Deal name" htmlFor='deal-name' required error={errors.name}>
                    <Input
                        id='deal-name'
                        value={form.name}
                        onChange={event => {
                            update('name', event.target.value)
                            if (errors.name) setErrors(current => ({ ...current, name: undefined }))
                        }}
                        placeholder="Platform expansion"
                        maxLength={120}
                        autoComplete='off'
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'deal-name-error' : undefined}
                        className='aria-invalid:border-danger'
                        autoFocus
                    />
                </Field>

                <Field label="Company" htmlFor='deal-company' required error={errors.companyId}>
                    <CompanySelect
                        id='deal-company'
                        value={form.companyId}
                        onChange={value => {
                            update('companyId', value)
                            if (errors.companyId) setErrors(current => ({ ...current, companyId: undefined }))
                        }}
                    />
                </Field>

                <div className='grid gap-4 sm:grid-cols-2'>
                    <Field label="Stage" htmlFor='deal-stage'>
                        <OptionSelect id='deal-stage' value={form.stage} onChange={value => update('stage', value)} options={STAGE_OPTIONS} />
                    </Field>
                    <Field label="Pipeline" htmlFor='deal-pipeline'>
                        <OptionSelect id='deal-pipeline' value={form.pipeline} onChange={value => update('pipeline', value)} options={PIPELINE_OPTIONS} />
                    </Field>
                </div>
            </FormSection>

            <FormSection title="Value & timing">
                <div className='grid gap-4 sm:grid-cols-2'>
                    <Field label="Deal value" htmlFor='deal-value'>
                        <div className='relative'>
                            <span aria-hidden className='text-subtle pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[14px] leading-none'>
                                $
                            </span>
                            <Input
                                id='deal-value'
                                type='number'
                                min={0}
                                step={1000}
                                inputMode='numeric'
                                value={form.value}
                                onChange={event => update('value', event.target.value)}
                                placeholder='120000'
                                className='pl-6 tabular-nums'
                            />
                        </div>
                    </Field>
                    <Field label="Close date" htmlFor='deal-close'>
                        <Input
                            id='deal-close'
                            type='date'
                            value={form.closeDate}
                            onChange={event => update('closeDate', event.target.value)}
                            className='tabular-nums'
                        />
                    </Field>
                </div>

                <Field label="Account owner" htmlFor='deal-owner'>
                    <OwnerSelect id='deal-owner' value={form.owner} onChange={value => update('owner', value)} />
                </Field>

                <Field label="Next step" htmlFor='deal-next-step' hint="Optional. Shown on the deal card.">
                    <Input
                        id='deal-next-step'
                        value={form.nextStep}
                        onChange={event => update('nextStep', event.target.value)}
                        placeholder="Send revised proposal"
                        maxLength={160}
                        autoComplete='off'
                        aria-describedby='deal-next-step-hint'
                    />
                </Field>
            </FormSection>

            {formError && (
                <div role='alert' className='caption-style text-danger px-6 pb-4'>
                    {formError}
                </div>
            )}

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant='subtle' size='sm'>
                        Cancel
                    </Button>
                </DialogClose>
                <Button variant='primary' size='sm' type='submit' disabled={submitting}>
                    {!editing && <PlusIcon aria-hidden className='size-3' />}
                    {submitting ? "Saving…" : editing ? "Save Deal" : "Create Deal"}
                </Button>
            </DialogFooter>
        </form>
    )
}

export default function DealDialog() {
    const open = useCrmStore(state => state.dialog === 'deal')
    const closeDialog = useCrmStore(state => state.closeDialog)

    return (
        <Dialog open={open} onOpenChange={next => !next && closeDialog()}>
            <DialogContent className='max-w-[560px]'>
                <DealForm />
            </DialogContent>
        </Dialog>
    )
}
